import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '@app/store/utils/baseQueryWithReauth';
import { ApiResponse } from '@app/store/utils/common';
import { extractData } from '@app/store/utils/common';
import { invalidateOnSuccess } from '@app/shared/utils/common';
import {
  AdminFindUserFilters,
  AdminFindUserResponse,
  AdminUpdateUserBalancePayload,
  AdminWithdrawalsFilters,
  AdminWithdrawalsResponse,
  AdminWithdrawalByIdResponse,
  AdminUpdateWithdrawalStatusPayload,
  AdminTagType,
  AdminBalanceAction,
} from './adminTypes';
import { PriceType } from '@app/shared/types/common';
import { nanoid } from '@reduxjs/toolkit';

const makeIdempotencyKey = () => {
  // modern browsers
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // fallback
  return nanoid();
};

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(AdminTagType),
  endpoints: (builder) => ({
    findUser: builder.query<AdminFindUserResponse | null, AdminFindUserFilters>({
      query: (params) => ({
        url: '/users/admin/find-user',
        params,
      }),
      transformResponse: extractData,
      providesTags: (result) => (result ? [{ type: AdminTagType.USER, id: result.id }] : []),
    }),
    updateUserBalance: builder.mutation<
      ApiResponse<void>,
      { body: AdminUpdateUserBalancePayload; findUserFilters?: AdminFindUserFilters }
    >({
      query: ({ body }) => {
        const idempotencyKey = makeIdempotencyKey();
        return {
          url: '/users/admin/update-balance',
          method: 'PATCH',
          body,
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        };
      },
      onQueryStarted({ body, findUserFilters }, { dispatch, queryFulfilled }) {
        if (!findUserFilters) {
          return;
        }

        void queryFulfilled.then(() => {
          const { balanceType, action, amount } = body;
          const { value, searchType } = findUserFilters;

          dispatch(
            adminApi.util.updateQueryData('findUser', { value, searchType }, (draft) => {
              if (!draft?.finance) {
                return;
              }

              const balanceKey = balanceType === PriceType.TON ? 'tonBalance' : 'cookieBalance';
              const currentBalance = Number(draft.finance[balanceKey]);

              if (action === AdminBalanceAction.TOP_UP) {
                draft.finance[balanceKey] = String(currentBalance + amount);
              } else /* if (action === AdminBalanceAction.WRITE_OFF) */ {
                draft.finance[balanceKey] = String(currentBalance - amount);
              }
            })
          );
        });
      },
    }),
    getWithdrawals: builder.query<AdminWithdrawalsResponse | null, AdminWithdrawalsFilters>({
      query: (params) => ({
        url: '/withdrawal/admin',
        params,
      }),
      transformResponse: extractData,
      providesTags: [AdminTagType.WITHDRAWAL],
    }),
    getWithdrawalById: builder.query<AdminWithdrawalByIdResponse | null, number>({
      query: (id) => `/withdrawal/admin/${String(id)}`,
      transformResponse: extractData,
      providesTags: (_, __, arg) => [{ type: AdminTagType.WITHDRAWAL, id: arg }],
    }),
    updateWithdrawalStatus: builder.mutation<ApiResponse<void>, AdminUpdateWithdrawalStatusPayload>(
      {
        query: (body) => ({
          url: '/withdrawal/admin/update-status',
          method: 'PATCH',
          body,
        }),
        invalidatesTags: invalidateOnSuccess([AdminTagType.WITHDRAWAL]),
      }
    ),
  }),
});

export const {
  useFindUserQuery,
  useUpdateUserBalanceMutation,
  useGetWithdrawalsQuery,
  useGetWithdrawalByIdQuery,
  useUpdateWithdrawalStatusMutation,
} = adminApi;
